// src/Pages/Blog/posts/train-ai-on-your-business.js
//   (was train-ai-on-your-business-2025.js; update the import in blogData.js)
//
// Rewritten 2026-10-17 (evergreen pass):
//   - Slug drops the year; the old slug redirects 301 via redirectFrom.
//   - Unverified statistics removed (CMI 82% / 5–10 hours, Invesp "80% of routine
//     inquiries"), and the Salesforce source that nothing in the text cited.
//   - Overclaims removed: "no correcting", "remembers everything perfectly",
//     "5–10 minutes" setup, "no ongoing maintenance". The post now says plainly that
//     the owner reviews and keeps the information current.
//   - Year-bound phrasing removed ("winning with AI in 2026", "five years ago").
//   - New practical section: a one-page checklist of what to give the AI.
//   - CTA is signup (was /landing/business). Author is named.

const post = {
  id: 7,
  slug: 'train-ai-on-your-business',
  redirectFrom: ['train-ai-on-your-business-2025'],
  title: 'Train AI on Your Business: Why Generic AI Gets Your Customers Wrong',
  excerpt: 'A generic AI tool knows nothing about your services, prices, policies or voice, so it writes confident answers that are wrong for your business. Here is what training AI on your business really means, what information to give it, and what changes when you do.',

  seo: {
    metaTitle: 'Train AI on Your Business: Stop Using Generic AI | Revfer',
    metaDescription: "Generic AI doesn't know your services, prices or policies, so it gets your customers wrong. What training AI on your business means, what to give it, and what changes when you do.",
    focusKeyword: 'train AI on your business',
    keywords: [
      'train AI on your business',
      'AI that knows my business',
      'custom AI for small business',
      'business-specific AI',
      'AI chatbot trained on my business',
      'AI customer service small business',
      'AI content in my brand voice',
      'small business AI context'
    ],
    canonicalUrl: 'https://www.revfer.com/blog/train-ai-on-your-business',
    ogImage: 'https://revfer-platform-photos-prod.s3.us-east-1.amazonaws.com/blogs/hero/train-ai-on-your-business-hero.png',
    twitterCard: 'summary_large_image',
  },

  // FAQ — rendered visibly at the end of the post and as FAQPage schema
  faq: [
    {
      question: 'What does it mean to train AI on your business?',
      answer: "For a small business it doesn't mean building a model. It means giving an AI tool your own information, such as your services, prices, hours, policies, common questions and the way you talk to customers, so it uses that every time it answers a customer, writes a post or replies to a review, instead of guessing.",
    },
    {
      question: 'Why does generic AI get small businesses wrong?',
      answer: "A general-purpose AI tool knows nothing about your business unless you tell it, and it doesn't remember from one session to the next. Ask it about your refund policy and it will write a polished answer based on what refund policies usually say, not what yours says. It sounds right and can be wrong, which is worse than no answer at all.",
    },
    {
      question: 'What information should I give an AI about my business?',
      answer: "Start with your services and prices, hours and location, your policies (cancellations, refunds, bookings, payment), the 20 questions customers ask most with your real answers, and a few examples of posts or messages you have written yourself so it can learn your voice. Menus, price lists and service guides are useful too.",
    },
    {
      question: 'Does AI trained on my business still need checking?',
      answer: "Yes. Even with your information, AI can misread something or fill a gap with a guess. Review what it writes, especially at the start, and keep your information current: when prices, hours or policies change, update them, or the AI will keep using the old ones.",
    },
    {
      question: 'What can AI do once it knows my business?',
      answer: "Answer routine customer questions at any hour using your real services and policies, write social media posts about what is actually happening in your business, draft replies to reviews that mention the specifics, and turn questions customers ask repeatedly into published FAQs. When all of these use the same information, they stay consistent with each other.",
    },
    {
      question: 'Do I need technical skills to train AI on my business?',
      answer: "No. With a tool built for small businesses, it is mostly a matter of filling in your business details and adding documents you already have, like a menu or price list. The work that matters is making sure the information is complete and correct, not anything technical.",
    },
  ],

  content: `
    <h2>The Problem With Generic AI</h2>

    <p>Open a general-purpose AI chatbot and ask it to answer a customer who wants to know your refund policy. It will write something polished, professional and confident. It may also be completely wrong, because it has no idea what your refund policy says. It wrote what refund policies <em>usually</em> say.</p>

    <p>Ask it for a social media post about your business and you get something that could describe any business of your type anywhere, because it doesn't know what makes yours different.</p>

    <p>This is the core problem with how many small businesses use AI today. The tools are capable, but they start from nothing every time. The fix isn't a cleverer prompt. It is giving the AI your actual business to work from.</p>

    <h2>What "Training AI on Your Business" Actually Means</h2>

    <p>"Training AI" sounds like data scientists and big budgets. For a small business, it means something much simpler: <strong>giving an AI tool your own information, once, so it uses that information every time it works for you</strong>.</p>

    <p>Think of it like onboarding a new employee. On their first day, you walk them through your services, your prices, your policies, how you talk to customers and the questions that come up all the time. A good new hire then represents your business accurately. They still check with you on anything unusual, and you still tell them when something changes.</p>

    <p>A business-trained AI works the same way. The difference is that it can answer a customer at 10 PM, write this week's posts and draft a reply to yesterday's review, all from the same information.</p>

    <h2>The Information That Makes the Difference</h2>

    <p>AI output is only as good as the information behind it. These are the things that matter most.</p>

    <h3>Your Services and Prices</h3>
    <p>This is the foundation. When a customer asks "do you do X, and how much is it?", the AI needs to know, not guess. Give it your full list of services or products, prices or price ranges, and anything specific, like what is included, how long it takes, or who it is for.</p>

    <h3>Your Policies</h3>
    <p>Cancellations, refunds, bookings, deposits, payment methods. Customers ask about these constantly, and a wrong answer creates real problems: a customer who turns up expecting a policy you never had. Give the AI your actual policies, in your words.</p>

    <h3>Your Most Common Questions</h3>
    <p>The questions you answer every week are the most valuable material you have. Write down the 20 questions customers ask most, by phone, email, message or in person, and your real answer to each. This one list will do more for accuracy than almost anything else.</p>

    <h3>Your Voice</h3>
    <p>A family bakery and a law firm can both use AI, but their customers would notice immediately if their voices were swapped. Give the AI a few examples of posts, emails or messages you have written yourself, and describe how you like to come across: warm or formal, playful or straightforward.</p>

    <h2>A One-Page Checklist</h2>

    <p>Before you set up any AI tool for your business, gather these. Most of it you already have.</p>
    <ul>
      <li>Business name, address, service area, phone, website and booking link</li>
      <li>Opening hours, including holidays and seasonal changes</li>
      <li>Services or products, with prices or price ranges</li>
      <li>Policies: cancellations, refunds, deposits, payment methods</li>
      <li>Your 20 most common customer questions, with your answers</li>
      <li>Three to five examples of things you have written to customers</li>
      <li>Anything that makes you different: experience, specialties, awards, the story behind the business</li>
      <li>Existing documents: menu, price list, service guide, brochure</li>
    </ul>

    <h2>What Changes When AI Knows Your Business</h2>

    <h3>Customer Questions Get Real Answers</h3>
    <p>A generic chatbot gives vague answers and fails on specific questions. One that knows your business answers the questions your customers actually ask, with the answers you have actually given them, including after you have closed for the day. Anything it doesn't know, or anything sensitive, it should hand to you rather than guess.</p>

    <h3>Posts Sound Like You, and Are About You</h3>
    <p>Generic AI writes generic posts you then have to rewrite. AI that knows your business writes posts about your actual products, offers and news, in a voice close to yours, so reviewing a draft takes minutes instead of rewriting it from scratch.</p>

    <h3>Review Replies Get Specific</h3>
    <p>"Thanks for your feedback!" tells future customers nothing. A reply that mentions the service the customer had, and responds to what they said, shows you read it. That needs an AI that knows what you offer.</p>

    <h3>Everything Stays Consistent</h3>
    <p>When customer answers, posts and review replies all draw on the same information, they don't contradict each other. The chatbot and this week's post quote the same hours. Change your prices once, and everything uses the new ones.</p>

    <h2>What AI Still Needs From You</h2>

    <p>Training AI on your business makes it far more accurate, but not infallible. Two habits keep it that way:</p>
    <ul>
      <li><strong>Review before anything goes out</strong>, especially in the first few weeks. Correct anything it gets wrong, and add the missing information so it doesn't happen again.</li>
      <li><strong>Keep your information current.</strong> When your hours, prices or policies change, update them. An AI working from last year's price list will quote last year's prices.</li>
    </ul>

    <blockquote>
      Generic AI answers customers' questions. AI that knows your business answers <em>your</em> customers' questions. That is the difference between a tool and something closer to a member of the team.
    </blockquote>

    <h2>What This Looks Like With Revfer</h2>

    <p>Revfer builds one shared picture of your business from the information you give it, and every part of the platform works from it. Your profile's chatbot answers customer questions around the clock with your real services, hours and prices. Social media posts for Facebook, Instagram, LinkedIn and Google Business Profile are drafted about what is actually happening in your business. Review replies mention the specifics. When customers keep asking the same question, Revfer turns it into a published FAQ. You review and approve, and when something changes, you tell Revfer once.</p>

    <p style="text-align: center; margin: 2rem 0;">
      <a href="/" style="display: inline-block; padding: 12px 24px; background: linear-gradient(135deg, #0D2B6B 0%, #1565C0 100%); color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 1.1rem;">Give AI your business to work from: start free →</a>
    </p>
  `,

  image: 'https://revfer-platform-photos-prod.s3.us-east-1.amazonaws.com/blogs/hero/train-ai-on-your-business-hero.png',

  author: {
    name: 'Pankaj Aggarwal',
    role: 'Founder, Revfer',
    avatar: 'https://revfer-platform-photos-prod.s3.us-east-1.amazonaws.com/blogs/pankaj.jpeg',
    bio: 'Pankaj Aggarwal is the founder of Revfer. He has spent more than 20 years building enterprise software and IoT systems and holds more than 10 US patents. He writes about how small businesses can put AI to work without having to become AI experts.',
  },

  categories: ['AI Automation', 'Customer Service', 'Business Operations'],

  tags: [
    'train AI on your business',
    'business-specific AI',
    'AI chatbot small business',
    'AI customer service',
    'AI content generation',
    'AI brand voice',
    'small business automation',
  ],

  publishedDate: '2026-10-20T09:00:00Z',
  modifiedDate: '2026-10-20T09:00:00Z',
  readTime: 6,
};

export default post;